import { Route, Routes, Link, Navigate, useLocation } from "react-router-dom";
import { useEffect, type ReactNode } from "react";
import AppLayout from "./AppLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { usePageTitle } from "@/lib/usePageTitle";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { trackPageView } from "@/lib/analytics";

/** Fires a PostHog $pageview on every route change */
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(window.location.href);
  }, [location.pathname, location.search]);
  return null;
}

import HomePage from "@/app/page";
import PricingPage from "@/app/pricing/page";
import ServersPage from "@/app/servers/page";
import ServerDetailPage from "@/app/servers/[slug]/page";
import ClaimServerPage from "@/app/servers/[slug]/claim/page";
import DownloadsPage from "@/app/downloads/page";
import DocsPage from "@/app/docs/page";
import LoginPage from "@/app/login/page";
import SignupPage from "@/app/signup/page";
import OnboardingPage from "@/app/onboarding/page";
import PluginsPage from "@/app/plugins/page";
import PluginUploadPage from "@/app/plugins/upload/page";
import PluginStarredPage from "@/app/plugins/starred/page";
import MyPluginsPage from "@/app/plugins/my-plugins/page";
import PluginPage from "@/app/plugins/[slug]/page";
import OrgPage from "@/app/org/[slug]/page";
import OrgSettingsPage from "@/app/org/[slug]/settings/page";
import ProfilePage from "@/app/profile/[username]/page";
import DashboardLayout from "@/app/(dashboard)/layout";
import DashboardPage from "@/app/(dashboard)/dashboard/page";
import DashboardJoinedPage from "@/app/(dashboard)/dashboard/joined/page";
import DashboardMyOrgsPage from "@/app/(dashboard)/dashboard/my-orgs/page";
import DashboardMyOrgsNewPage from "@/app/(dashboard)/dashboard/my-orgs/new/page";
import DashboardSettingsPage from "@/app/(dashboard)/dashboard/settings/page";
import BillingSettingsPage from "@/app/(dashboard)/dashboard/settings/billing/page";
import DashboardBillingPage from "@/app/(dashboard)/dashboard/billing/page";
import SubscriptionManagementPage from "@/app/(dashboard)/dashboard/subscription/page";
import DashboardServersPage from "@/app/(dashboard)/dashboard/servers/page";
import AdvancedStatsPage from "@/app/(dashboard)/dashboard/advanced-stats/page";
import PriorityQueuePage from "@/app/(dashboard)/dashboard/priority-queue/page";
import BillingSuccessPage from "@/app/billing/success/page";
import BillingCancelPage from "@/app/billing/cancel/page";
import PaymentSuccessPage from "@/app/payment/success/page";
import PaymentCancelPage from "@/app/payment/cancel/page";
import CheckoutSuccessPage from "@/app/success/page";
import CheckoutCancelPage from "@/app/cancel/page";
import CheckoutSuccessV2Page from "@/app/checkout/success/page";
import AccountBillingPage from "@/app/account/billing/page";
import AccountSubscriptionPage from "@/app/account/subscription/page";
import AccountSubscriptionCancelledPage from "@/app/account/subscription/cancelled/page";
import AdminOverviewPage from "@/app/admin/page";
import AdminBillingPage from "@/app/admin/billing/page";
import AdminSubscriptionsPage from "@/app/admin/subscriptions/page";
import CookiePolicyPage from "@/app/cookie-policy/page";
import PrivacyPolicyPage from "@/app/privacy-policy/page";
import TermsOfServicePage from "@/app/terms-of-service/page";
import RefundPolicyPage from "@/app/refund-policy/page";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { SubscriptionGate } from "@/components/auth/SubscriptionGate";
import { ServerProvisioningGate } from "@/components/auth/ServerProvisioningGate";

function NotFoundPage() {
  usePageTitle("Page Not Found");
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />
      <main className="flex flex-1 flex-col items-center justify-center px-4 py-16 text-center">
        <h1 className="mb-2 text-6xl font-bold text-foreground">404</h1>
        <h2 className="mb-4 text-2xl font-semibold text-foreground">
          Page Not Found
        </h2>
        <p className="mb-8 max-w-md text-muted">
          The page you are looking for does not exist or has been moved. Check the
          URL or head back to the home page.
        </p>
        <Link
          to="/"
          className="rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Go Home
        </Link>
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  const withAuth = (children: ReactNode) => (
    <RequireAuth>{children}</RequireAuth>
  );

  // Any paid tier required
  const withSubscription = (children: ReactNode) => (
    <SubscriptionGate>{children}</SubscriptionGate>
  );

  // Founder Plus or above required
  const withPlusSubscription = (children: ReactNode) => (
    <SubscriptionGate minTier="plus">{children}</SubscriptionGate>
  );

  // Founder Pro or above required
  const withProSubscription = (children: ReactNode) => (
    <SubscriptionGate minTier="pro">{children}</SubscriptionGate>
  );

  return (
    <ErrorBoundary>
      <AppLayout>
        <PageViewTracker />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          {/* /upgrade is an alias for /pricing — subscription gate redirects land here */}
          <Route path="/upgrade" element={<Navigate to="/pricing" replace />} />
          <Route path="/servers" element={<ServersPage />} />
          <Route path="/servers/:slug" element={<ServerDetailPage />} />
          <Route path="/servers/:slug/claim" element={withAuth(<ClaimServerPage />)} />
          <Route path="/downloads" element={<DownloadsPage />} />
          <Route path="/docs" element={<Navigate to="/documentation" replace />} />
          <Route path="/documentation" element={<DocsPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/plugins" element={<PluginsPage />} />
          <Route path="/plugins/upload" element={<PluginUploadPage />} />
          <Route path="/plugins/starred" element={<PluginStarredPage />} />
          <Route path="/plugins/my-plugins" element={<MyPluginsPage />} />
          <Route path="/plugins/official-companion" element={<Navigate to="/downloads" replace />} />
          <Route path="/plugins/:slug" element={<PluginPage />} />
          <Route path="/profile/:username" element={<ProfilePage />} />
          <Route path="/org/:slug" element={<OrgPage />} />
          {/* ── Subscription-gated routes (any paid tier) ─────────────── */}
          <Route path="/org/:slug/settings" element={withSubscription(<OrgSettingsPage />)} />
          <Route path="/dashboard" element={withSubscription(<DashboardLayout><DashboardPage /></DashboardLayout>)} />
          <Route path="/dashboard/joined" element={withSubscription(<DashboardLayout><DashboardJoinedPage /></DashboardLayout>)} />
          <Route path="/dashboard/my-orgs" element={withSubscription(<DashboardLayout><DashboardMyOrgsPage /></DashboardLayout>)} />
          <Route path="/dashboard/my-orgs/new" element={withSubscription(<DashboardLayout><DashboardMyOrgsNewPage /></DashboardLayout>)} />
          {/* ── Subscription-gated routes (Plus tier or above) ────────── */}
          <Route path="/dashboard/advanced-stats" element={withPlusSubscription(<DashboardLayout><AdvancedStatsPage /></DashboardLayout>)} />
          {/* ── Subscription-gated routes (Pro tier or above) ─────────── */}
          <Route path="/dashboard/priority-queue" element={withProSubscription(<DashboardLayout><PriorityQueuePage /></DashboardLayout>)} />
          <Route path="/dashboard/servers" element={<ServerProvisioningGate><DashboardLayout><DashboardServersPage /></DashboardLayout></ServerProvisioningGate>} />
          <Route path="/dashboard/billing" element={withAuth(<DashboardLayout><DashboardBillingPage /></DashboardLayout>)} />
          <Route path="/dashboard/subscription" element={withAuth(<DashboardLayout><SubscriptionManagementPage /></DashboardLayout>)} />
          <Route path="/dashboard/settings" element={withAuth(<DashboardLayout><DashboardSettingsPage /></DashboardLayout>)} />
          <Route path="/dashboard/settings/billing" element={withAuth(<DashboardLayout><BillingSettingsPage /></DashboardLayout>)} />
          <Route path="/checkout/success" element={withAuth(<DashboardLayout><CheckoutSuccessV2Page /></DashboardLayout>)} />
          <Route path="/billing/success" element={withAuth(<DashboardLayout><BillingSuccessPage /></DashboardLayout>)} />
          <Route path="/billing/cancel" element={withAuth(<DashboardLayout><BillingCancelPage /></DashboardLayout>)} />
          <Route path="/payment/success" element={withAuth(<DashboardLayout><PaymentSuccessPage /></DashboardLayout>)} />
          <Route path="/payment/cancel" element={withAuth(<DashboardLayout><PaymentCancelPage /></DashboardLayout>)} />
          <Route path="/account/billing" element={withAuth(<DashboardLayout><AccountBillingPage /></DashboardLayout>)} />
          <Route path="/account/subscription" element={withAuth(<DashboardLayout><AccountSubscriptionPage /></DashboardLayout>)} />
          <Route path="/account/subscription/cancelled" element={withAuth(<DashboardLayout><AccountSubscriptionCancelledPage /></DashboardLayout>)} />
          <Route path="/success" element={<CheckoutSuccessPage />} />
          <Route path="/cancel" element={<CheckoutCancelPage />} />
          <Route path="/admin" element={withAuth(<AdminOverviewPage />)} />
          <Route path="/admin/billing" element={withAuth(<AdminBillingPage />)} />
          <Route path="/admin/subscriptions" element={withAuth(<AdminSubscriptionsPage />)} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AppLayout>
    </ErrorBoundary>
  );
}
